import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, Search, Send, X } from "lucide-react";

/**
 * CommunityDmInviteModal — owner/mod flow to DM a specific invite link
 * to another user. Reuses `GET /api/users?q=…` for typeahead and
 * `POST /api/messages` to actually send the DM, so no new backend
 * surface is needed.
 *
 * Prop `inviteUrl` is the fully-formed `mypandax.com/c/<id>?invite=<code>`
 * URL — the modal simply drops it into a friendly message body along
 * with the community name.
 */
export default function CommunityDmInviteModal({ open, onClose, inviteUrl, communityName }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [sent, setSent] = useState(new Set());

  // Debounced typeahead.
  useEffect(() => {
    if (!open) return;
    if (!q.trim()) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/users`, { params: { q: q.trim(), limit: 8 } });
        if (!cancelled) setResults(Array.isArray(data) ? data : (data.users || []));
      } catch { if (!cancelled) setResults([]); }
      finally { if (!cancelled) setSearching(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, open]);

  // Reset transient state when the modal closes so re-open feels clean.
  useEffect(() => {
    if (!open) {
      setQ("");
      setResults([]);
      setSent(new Set());
    }
  }, [open]);

  if (!open) return null;

  const send = async (target) => {
    if (sendingId) return;
    setSendingId(target.id);
    try {
      const text =
        `You're invited to ${communityName} on my panda x. ` +
        `Click to join in one tap: ${inviteUrl}`;
      await api.post("/messages", { to_id: target.id, text });
      toast.success(`Invite sent to ${target.display_name}`);
      setSent((prev) => new Set(prev).add(target.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send DM.");
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="community-dm-invite-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-zinc-950 border border-white/10 rounded-2xl overflow-hidden flex flex-col max-h-[80vh]"
      >
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Send size={14} className="text-rose-400" />
            <div className="text-sm font-semibold text-white">Send invite via DM</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="community-dm-invite-close"
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white flex items-center justify-center"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>
        <div className="px-5 py-3 border-b border-white/5">
          <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-md px-3 py-2">
            <Search size={13} className="text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by handle or name…"
              data-testid="community-dm-invite-search"
              className="flex-1 bg-transparent text-sm outline-none"
              autoFocus
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {searching && (
            <div className="text-center text-xs text-zinc-500 py-8">
              <Loader2 size={12} className="inline animate-spin mr-1" /> Searching…
            </div>
          )}
          {!searching && q.trim() && results.length === 0 && (
            <div className="text-center text-xs text-zinc-500 py-8">
              No one matches "{q.trim()}"
            </div>
          )}
          {!searching && !q.trim() && (
            <div className="text-center text-xs text-zinc-600 py-8 px-6 leading-relaxed">
              Start typing a handle or name to find who you'd like to invite. They'll receive a DM
              with a one-tap join link.
            </div>
          )}
          <ul>
            {results.map((u) => {
              const isSent = sent.has(u.id);
              const isBusy = sendingId === u.id;
              return (
                <li
                  key={u.id}
                  data-testid={`community-dm-invite-row-${u.id}`}
                  className="flex items-center gap-3 px-5 py-2.5 hover:bg-white/5"
                >
                  <Avatar className="w-9 h-9 border border-white/10">
                    <AvatarImage src={u.avatar_url} />
                    <AvatarFallback className="bg-rose-600 text-white text-xs">
                      {u.display_name?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{u.display_name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{u.display_name}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => send(u)}
                    disabled={isBusy || isSent}
                    data-testid={`community-dm-invite-send-${u.id}`}
                    className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-widest rounded-full px-3 py-1.5 ${
                      isSent
                        ? "bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 cursor-default"
                        : "bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white"
                    }`}
                  >
                    {isBusy ? <Loader2 size={11} className="animate-spin" /> : isSent ? "Sent" : <>Send <Send size={10} /></>}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
